/*
 * Smart Bottom Promo — independent component, no dependency on custom.js/global.min.js.
 * States: HIDDEN -> OPEN -> MINIMIZED -> DISMISSED (see class body for transitions).
 */
(function () {
  "use strict";

  if (customElements.get("sys-promo")) return;

  var STATE = {
    HIDDEN: "HIDDEN",
    OPEN: "OPEN",
    MINIMIZED: "MINIMIZED",
    DISMISSED: "DISMISSED",
  };

  function setCookie(name, value, days) {
    try {
      var expires = "";
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie = name + "=" + (value || "") + expires + "; path=/";
    } catch (e) {}
  }

  function getCookie(name) {
    try {
      var match = document.cookie.match(
        new RegExp("(^| )" + name + "=([^;]+)")
      );
      return match ? decodeURIComponent(match[2]) : null;
    } catch (e) {
      return null;
    }
  }

  function scrollProgress() {
    var doc = document.documentElement;
    var scrollable = doc.scrollHeight - doc.clientHeight;
    if (scrollable <= 0) return 0;
    return (window.scrollY / scrollable) * 100;
  }

  // Best-effort, point-in-time only (no persistent observer): is something else
  // already claiming the user's attention right now? Checked only at the moment
  // we're about to open, and during the bounded retry below — never polled
  // continuously in the background.
  function isBlocked() {
    if (document.querySelector(".hdt-drawer[open]")) return true;
    if (document.documentElement.classList.contains("no-scroll")) return true;

    // Smile Rewards: the launcher (#smile-ui-container) is always in the DOM;
    // treat its panel iframe as "open" if it currently has real on-screen size.
    // NOTE: inferred from the theme's own overrides (assets/theme.css), not
    // confirmed against Smile's live markup — verify on the storefront if this
    // ever seems to under/over-trigger.
    var smilePanel = document.querySelector(".smile-panel-frame-container");
    if (smilePanel && smilePanel.offsetWidth > 0 && smilePanel.offsetHeight > 0) {
      return true;
    }
    return false;
  }

  var SmartPromo = /** @class */ (function () {
    function SmartPromo(el) {
      this.el = el;
      this.timers = {};
      this.state = STATE.HIDDEN;
      this.config = {};
      try {
        this.config = JSON.parse(el.getAttribute("configs") || "{}");
      } catch (e) {
        this.config = {};
      }
      this.sectionId = this.config.id || "";
      this.dismissKey = "theme4:promo:" + this.sectionId;
      this.minimizedKey = "theme4:promo:" + this.sectionId + ":state";
      this.card = el.querySelector(".hdt-promo-card");
      this.tab = el.querySelector(".hdt-promo-tab");
    }

    SmartPromo.prototype.clearTimer = function (name) {
      if (this.timers[name]) {
        clearTimeout(this.timers[name]);
        delete this.timers[name];
      }
    };

    SmartPromo.prototype.clearAllTimers = function () {
      var self = this;
      Object.keys(this.timers).forEach(function (name) {
        self.clearTimer(name);
      });
      if (this._onScroll) {
        window.removeEventListener("scroll", this._onScroll);
        this._onScroll = null;
      }
    };

    SmartPromo.prototype.init = function () {
      if (!this.card || !this.tab) return;

      if (window.Shopify && Shopify.designMode) {
        this.bindDesignMode();
        return;
      }

      if (!this.config.always_show) {
        if (getCookie(this.dismissKey) === "dismissed") {
          this.state = STATE.DISMISSED;
          return;
        }

        var minimized = false;
        try {
          minimized = sessionStorage.getItem(this.minimizedKey) === "1";
        } catch (e) {}

        if (minimized) {
          this.minimize(true);
          return;
        }
      }

      this.setupTrigger();
    };

    SmartPromo.prototype.setupTrigger = function () {
      var self = this;
      var delaySeconds = Number(this.config.trigger_delay) || 10;
      var scrollPercent = Number(this.config.trigger_scroll_percent) || 35;
      var fired = false;

      function fire() {
        if (fired) return;
        fired = true;
        self.clearAllTimers();
        self.requestOpen();
      }

      this.timers.trigger = setTimeout(fire, delaySeconds * 1000);

      this._onScroll = function () {
        if (scrollProgress() >= scrollPercent) fire();
      };
      window.addEventListener("scroll", this._onScroll, { passive: true });
    };

    // Opens as soon as nothing else (drawer/modal/Smile panel) is in the way.
    // Bounded, coarse retry (1s ticks, capped) — not a tight/continuous poll.
    // Once whatever was blocking us clears, we wait a short settle buffer
    // (per spec: ~3-5s) and re-verify before actually opening, so the promo
    // never appears the instant Smile/a drawer closes.
    SmartPromo.prototype.requestOpen = function () {
      var self = this;
      if (this.state === STATE.DISMISSED) return;

      if (isBlocked()) {
        this._waitedForBlocker = true;
        this._blockedAttempts = (this._blockedAttempts || 0) + 1;
        if (this._blockedAttempts >= 20) {
          // Give up waiting after ~20s; show rather than vanish silently.
          this.open();
          return;
        }
        this.clearTimer("blockedRetry");
        this.timers.blockedRetry = setTimeout(function () {
          self.requestOpen();
        }, 1000);
        return;
      }

      if (this._waitedForBlocker) {
        this._waitedForBlocker = false;
        this.clearTimer("blockedRetry");
        this.timers.blockedRetry = setTimeout(function () {
          self.requestOpen();
        }, 4000);
        return;
      }

      this.open();
    };

    SmartPromo.prototype.open = function () {
      if (this.state === STATE.DISMISSED) return;
      this.state = STATE.OPEN;
      this.tab.hidden = true;
      this.tab.removeAttribute("data-open");
      this.tab.setAttribute("aria-expanded", "false");
      this.card.hidden = false;
      // reflow before toggling so the transition runs
      void this.card.offsetWidth;
      this.card.setAttribute("data-open", "");

      var self = this;
      var minimizeAfter = Number(this.config.auto_minimize_seconds) || 8;
      this.clearTimer("autoMinimize");
      this.timers.autoMinimize = setTimeout(function () {
        self.minimize();
      }, minimizeAfter * 1000);
    };

    SmartPromo.prototype.minimize = function (skipCardHide) {
      if (this.state === STATE.DISMISSED) return;
      this.state = STATE.MINIMIZED;
      this.clearTimer("autoMinimize");

      if (!skipCardHide) {
        this.card.removeAttribute("data-open");
      }
      this.card.hidden = true;
      this.tab.hidden = false;
      void this.tab.offsetWidth;
      this.tab.setAttribute("data-open", "");
      this.tab.setAttribute("aria-expanded", "false");

      try {
        sessionStorage.setItem(this.minimizedKey, "1");
      } catch (e) {}
    };

    SmartPromo.prototype.expand = function () {
      if (this.state === STATE.DISMISSED) return;
      this.open();
    };

    SmartPromo.prototype.dismiss = function () {
      this.state = STATE.DISMISSED;
      this.clearAllTimers();
      this.card.removeAttribute("data-open");
      this.tab.removeAttribute("data-open");
      this.card.hidden = true;
      this.tab.hidden = true;
      var days = Number(this.config.day_next) || 3;
      setCookie(this.dismissKey, "dismissed", days);
      try {
        sessionStorage.removeItem(this.minimizedKey);
      } catch (e) {}
    };

    SmartPromo.prototype.bindDesignMode = function () {
      var self = this;
      this.card.hidden = false;
      this.tab.hidden = true;
      document.addEventListener("shopify:section:select", function (e) {
        if (e.detail && e.detail.sectionId === self.sectionId) {
          self.card.setAttribute("data-open", "");
        }
      });
      document.addEventListener("shopify:section:deselect", function (e) {
        if (e.detail && e.detail.sectionId === self.sectionId) {
          self.card.removeAttribute("data-open");
        }
      });
    };

    SmartPromo.prototype.bindEvents = function () {
      var self = this;
      var closeBtn = this.card.querySelector("[data-promo-close]");
      if (closeBtn) {
        closeBtn.addEventListener("click", function () {
          self.dismiss();
        });
      }
      this.tab.addEventListener("click", function () {
        self.expand();
      });
    };

    SmartPromo.prototype.connectedCallback = function () {
      try {
        this.bindEvents();
        this.init();
      } catch (e) {
        // A failure here must never break the rest of the site — leave
        // everything in its default hidden state.
        if (window.console && console.error) console.error("[smart-promo]", e);
      }
    };

    return SmartPromo;
  })();

  customElements.define(
    "sys-promo",
    class extends HTMLElement {
      connectedCallback() {
        this._instance = new SmartPromo(this);
        this._instance.connectedCallback();
      }
    }
  );
})();
